//@ts-nocheck
import React, { FC, useState, useContext, useCallback } from 'react';
import BeerXchangeApi from '../services/BeerXchangeApi';
import { UntappdUser } from '../types/model/untappdUser';
import { BeerXchangeCollection } from '../types/model/beerXchangeCollection';
import { Beer } from '../types/model/beer';
import { ClaimedBeer } from '../types/model/claimedBeer';
import { Brewery } from '../types/model/brewery';
import { BeerXchangeUser } from '../types/model/beerXchangeUser';
import { BeerWithClaims } from '../types/model/beerWithClaims';
import { Trade } from '../types/model/trade';
import { Offer } from '../types/model/offer';
import { Seeking } from '../types/model/seeking';
import { UserEvents } from '../types/model/userEvents';
import { Shipping } from '../types/model/shipping';
import { DirectoryIndex, DirectoryPage } from '../types/model/directory';

const uniqBy = require('lodash.uniqby');

export interface Dictionary {
  id: string;
  name: string;
}

export interface BeerXchangeContextType {
  untappdAccessToken: string;
  loggedInUntappdUser: UntappdUser;
  loggedInUser: BeerXchangeUser;
  logOutUser(): void;
  refreshLoggedInUserCollection: () => void;
  refreshLoggedInUserEvents: () => void;
  isRefreshing: boolean;
  loggedInUserCollection: BeerXchangeCollection;
  loggedInUserClaims: BeerWithClaims[];
  knownBeers: Beer[];
  getBeer: Beer[];
  userSeekingBeers: Beer[];
  userOfferingBeers: Beer[];
  knownBreweries: Brewery[];
  searchBeers: (query: string) => Promise<Beer[]>;
  searchBreweries: (query: string) => Promise<Brewery[]>;
  getBrewery: (breweryUntappdId: string) => Promise<Brewery>;
  getBreweryBeers: (breweryUntappdId: string) => Promise<Beer[]>;
  claimBeer: (beerUntappdId: string) => void;
  unclaimBeer: (beerId: string) => void;
  getUser: () => Promise<BeerXchangeUser>;
  getUsers: () => Promise<BeerXchangeUser[]>;
  knownUsers: BeerXchangeUser[];
  getTrade: (tradeId: string) => Promise<Trade>;
  getTradeList: (userId: string) => Promise<Trade[]>;
  getTradeHistory: (userId: string) => Promise<Trade[]>;
  knownCurrentTrades: Trade[];
  knownHistoryTrades: Trade[];
  loggedInUserEvents: UserEvents[];
  previousSearches: string[];
  seekBeer: (beerUntappdId: string) => Beer;
  offerBeer: (beerUntappdId: string) => Beer;
  acceptOffer: (offerId: string, notes: string) => Offer;
  cancelOffer: (offerId: string, notes: string) => Offer;
  rejectOffer: (offerId: string, notes: string) => Offer;
  makeOffer: (
    userName: string,
    offeredBeers: string[],
    requestedBeers: string[],
    notes: string,
    ttl: string
  ) => Offer;
  updateOffer: (
    offerId: string,
    tradeId: string,
    offeredBeers: string[],
    requestedBeers: string[],
    notes: string,
    ttl: string
  ) => Offer;
  beginShipping: (
    offerId: string,
    shippingType: string,
    carrier: string,
    trackingNumber: string,
    shippingCost: string,
    notes: string
  ) => Offer;
  markComplete: (
    tradeId: string,
    ratingValue: string,
    timelyShipping: string,
    communicatesWell: string,
    helpful: string,
    wouldTradeAgain: string,
    notes: string
  ) => Trade;
  markException: (
    tradeId: string,
    ratingValue: string,
    timelyShipping: string,
    communicatesWell: string,
    helpful: string,
    wouldTradeAgain: string,
    notes: string
  ) => Trade;
  isUserSeekingBeer: (beer: Beer) => boolean;
  isUserOfferingBeer: (beer: Beer) => boolean;
  userSeekingBreweries: Brewery[];
  userOfferingBreweries: Brewery[];
  isUserSeekingBrewery: (brewery: Brewery) => boolean;
  isUserOfferingBrewery: (brewery: Brewery) => boolean;
  getMostWanted: () => Promise<Beer[]>;
  getMostOffered: () => Promise<Beer[]>;
  getDirectoryIndex: () => Promise<DirectoryIndex>;
  getDirectoryPage: (letter: string, page: number) => Promise<DirectoryPage>;
  locationList: Dictionary[];
  purchasedList: Dictionary[];
  collectionFilter: string;
  collectionPredicate: string;
  collectionReverse: boolean;
  importCollection: (collectionId: string, file: File) => Promise<any[]>;
}

export const BeerXchangeContext = React.createContext<Partial<BeerXchangeContext>>({
  loggedInUserClaims: [],
  isRefreshing: false,
  knownBeers: [],
  knownBreweries: [],
  knownUsers: [],
  knownCurrentTrades: [],
  knownHistoryTrades: [],
  loggedInUserEvents: [],
  previousSearches: [],
  userSeekingBeers: [],
  userOfferingBeers: [],
  userSeekingBreweries: [],
  userOfferingBreweries: [],
  locationList: [],
  purchasedList: [],
  collectionFilter: localStorage.getItem('collectionFilter')
    ? localStorage.getItem('collectionFilter')
    : '',
  collectionPredicate: localStorage.getItem('collectionPredicate')
    ? localStorage.getItem('collectionPredicate')
    : 'name',
  collectionReverse: localStorage.getItem('collectionReverse')
    ? localStorage.getItem('collectionReverse')
    : 'false',
});

//export const BeerXchangeContext = React.createContext<BeerXchangeContextType | null>(null);

type BeerXchangeProviderProps = {
  children: React.ReactNode;
  untappdAccessToken: string;
  loggedInUntappdUser: UntappdUser;
  logOutUser: () => void;
  loggedInUser: BeerXchangeUser;
};

export const BeerXchangeProvider: FC<BeerXchangeProviderProps> = ({
  children,
  untappdAccessToken,
  loggedInUntappdUser,
  loggedInUser,
  logOutUser,
}) => {
  const [state, setState] = useState<Partial<BeerXchangeContext>>({
    loggedInUntappdUser,
    loggedInUser,
    logOutUser,
    knownBeers: [],
    knownBreweries: [],
    knownUsers: [],
    knownCurrentTrades: [],
    knownHistoryTrades: [],
    previousSearches: [],
    userSeekingBeers: [],
    userOfferingBeers: [],
    userSeekingBreweries: [],
    userOfferingBreweries: [],
    locationList: [],
    purchasedList: [],
    collectionFilter: localStorage.getItem('collectionFilter')
      ? localStorage.getItem('collectionFilter')
      : '',
    collectionPredicate: localStorage.getItem('collectionPredicate')
      ? localStorage.getItem('collectionPredicate')
      : 'name',
    collectionReverse: localStorage.getItem('collectionReverse')
      ? localStorage.getItem('collectionReverse')
      : 'false',
  });

  const beerXchangeApi = new BeerXchangeApi(untappdAccessToken, loggedInUntappdUser?.id);

  const setCollectionFilter = useCallback((filter: string) => {
    localStorage.setItem('collectionFilter', filter);
    setState((state) => ({ ...state, collectionFilter: filter }));
  }, []);

  const setCollectionPredicate = (predicate: string) => {
    localStorage.setItem('collectionPredicate', predicate);
    setState((state) => ({ ...state, collectionPredicate: predicate }));
  };

  const setCollectionReverse = (reverse: string) => {
    localStorage.setItem('collectionReverse', reverse);
    setState((state) => ({ ...state, collectionReverse: reverse }));
  };

  const updateKnownBeers = (newBeers: Beer[]) => {
    if (newBeers && newBeers.length > 0) {
      const joinedBeers = uniqBy(
        state.knownBeers!.concat(newBeers),
        (beer: Beer) => beer.untappdId
      );
      const newBreweries = newBeers.map((beer) => beer.brewery);
      const joinedBreweries = Array.from(new Set(state.knownBreweries!.concat(newBreweries)));
      setState((state) => ({
        ...state,
        knownBeers: joinedBeers,
        knownBreweries: joinedBreweries,
        isRefreshing: false,
      }));
    }
  };

  const updateKnownBreweries = (newBrewery: Brewery) => {
    const newBreweries = [newBrewery];
    const joinedBreweries = Array.from(new Set(state.knownBreweries!.concat(newBreweries)));
    setState((state) => ({ ...state, knownBreweries: joinedBreweries, isRefreshing: false }));
  };

  const isUserSeekingBeer = (beer: Beer) => {
    var result = false;
    if (beer && state.userSeekingBeers && state.userSeekingBeers.length > 0) {
      result = state.userSeekingBeers.some((seeking) => seeking.untappdId === beer.untappdId);
    }
    return result;
  };

  const isUserOfferingBeer = (beer: Beer) => {
    var result = false;
    if (beer && state.userOfferingBeers && state.userOfferingBeers.length > 0) {
      result = state.userOfferingBeers.some((offering) => offering.untappdId === beer.untappdId);
    }
    return result;
  };

  const updateKnownUsers = (newUsers: BeerXchangeUser[]) => {
    if (newUsers && newUsers.length > 0) {
      const joinedUsers = uniqBy(
        state.knownUsers!.concat(newUsers),
        (user: BeerXchangeUser) => user.untappdId
      );
      setState((state) => ({ ...state, knownUsers: joinedUsers, isRefreshing: false }));
    }
  };

  const updateKnownCurrentTrades = (newTrades: Trade[]) => {
    if (newTrades && newTrades.length > 0) {
      const joinedTrades = uniqBy(
        state.knownCurrentTrades!.concat(newTrades),
        (trade: Trade) => trade.id
      );
      setState((state) => ({ ...state, knownCurrentTrades: joinedTrades, isRefreshing: false }));
    }
  };

  const updateKnownHistoryTrades = (newTrades: Trade[]) => {
    if (newTrades && newTrades.length > 0) {
      const joinedTrades = uniqBy(
        state.knownHistoryTrades!.concat(newTrades),
        (trade: Trade) => trade.id
      );
      setState((state) => ({ ...state, knownHistoryTrades: joinedTrades, isRefreshing: false }));
    }
  };

  const updatePreviousSearches = (newSearch: string) => {
    if (newSearch && newSearch.length >= 3) {
      const joinedSearches = uniqBy(
        [newSearch].concat(state.previousSearches!),
        (search: String) => search
      );
      setState((state) => ({ ...state, previousSearches: joinedSearches, isRefreshing: false }));
    }
  };

  const removePreviousSearch = (newSearch: string) => {
    var previousSearches = state.previousSearches!;
    const index = previousSearches.indexOf(newSearch);
    if (index > -1) {
      previousSearches.splice(index, 1);
    }
    setState((state) => ({ ...state, previousSearches }));
  };

  const clearPreviousSearches = (newSearch: string) => {
    setState((state) => ({ ...state, previousSearches: [] }));
  };

  const refreshLoggedInUser = () => {
    beerXchangeApi
      .getUserInfo(loggedInUntappdUser.user_name)
      .then((response) => {
        setState((state) => ({ ...state, loggedInUser: response, isRefreshing: false }));
      })
      .catch((error: any) => {
        console.warn('http error:', error, error.stack);
        setState((state) => ({ ...state, isRefreshing: false }));
      });
  };

  const refreshLoggedInUserClaims = () => {
    beerXchangeApi
      .getClaimsForUser(loggedInUntappdUser.user_name)
      .then((response) => {
        updateKnownBeers(response);
        updateUserLists(response);
        setState((state) => ({ ...state, loggedInUserClaims: response, isRefreshing: false }));
      })
      .catch((error: any) => {
        console.warn('http error:', error, error.stack);
        setState((state) => ({ ...state, isRefreshing: false }));
      });
  };

  const updateUserLists = (loggedInUserClaims: ClaimedBeer[]) => {
    let loc_array = [];
    let purc_array = [];
    let locationList = [];
    let purchasedList = [];

    loggedInUserClaims.map((beer: ClaimedBeer) => {
      beer.claims.map((beer) => {
        if (beer.location && loc_array.indexOf(beer.location) === -1) {
          loc_array.push(beer.location);
        }

        if (beer.purchasedFrom && purc_array.indexOf(beer.purchasedFrom) === -1) {
          purc_array.push(beer.purchasedFrom);
        }
      });
    });

    loc_array.sort().map((loc: string) => {
      let item = { id: loc, name: loc };
      locationList.push(item);
    });

    purc_array.sort().map((purc: string) => {
      let item = { id: purc, name: purc };
      purchasedList.push(item);
    });

    locationList.push({ id: 'AddLocation', name: 'Add/Edit Location text' });
    purchasedList.push({ id: 'AddPurchased', name: 'Add/Edit Purchased text' });
    setState((state) => ({ ...state, locationList }));
    setState((state) => ({ ...state, purchasedList }));
  };

  const refreshLoggedInUserCollection = () => {
    try {
      setState((state) => ({ ...state, isRefreshing: true }));
      beerXchangeApi
        .getCollectionForUser(loggedInUntappdUser.user_name)
        .then((response) => {
          setState((state) => ({
            ...state,
            loggedInUserCollection: response,
            isRefreshing: false,
          }));
        })
        .catch((error: any) => {
          console.warn('http error:', error, error.stack);
          setState((state) => ({ ...state, isRefreshing: false }));
        });
    } catch (error) {
      console.warn('caught error:', error, error.stack);
    }
  };

  const refreshLoggedInUserEvents = () => {
    try {
      setState((state) => ({ ...state, isRefreshing: true }));
      beerXchangeApi
        .getUserEvents(loggedInUser.id.toString())
        .then((response) => {
          setState((state) => ({ ...state, loggedInUserEvents: response, isRefreshing: false }));
        })
        .catch((error: any) => {
          console.warn('http error:', error, error.stack);
          setState((state) => ({ ...state, isRefreshing: false }));
        });
    } catch (error) {
      console.warn('caught error:', error, error.stack);
    }
  };

  const searchBeers = (query: string): Promise<Beer[]> => {
    try {
      if (query != null && query.length > 2) {
        setState((state) => ({ ...state, isRefreshing: true }));
        const returnPromise = beerXchangeApi.searchBeers(query);
        returnPromise
          .then((response) => {
            updateKnownBeers(response);
            updatePreviousSearches(query);
          })
          .catch((error: any) => {
            console.warn('http error:', error, error.stack);
            setState((state) => ({ ...state, isRefreshing: false }));
            throw error;
          });
        return returnPromise;
      }
    } catch (error) {
      console.warn('caught error:', error, error.stack);
      throw error;
    }
  };

  const searchBreweries = (query: string): Promise<Brewery[]> => {
    try {
      if (query.length > 2) {
        setState((state) => ({ ...state, isRefreshing: true }));
        const returnPromise = beerXchangeApi.searchBreweries(query);
        return returnPromise;
      }
    } catch (error) {
      console.warn('caught error:', error, error.stack);
      setState((state) => ({ ...state, isRefreshing: false }));
      throw error;
    }
  };

  const getBrewery = (breweryUntappdId: string): Promise<Brewery> => {
    try {
      setState((state) => ({ ...state, isRefreshing: true }));
      const returnPromise = beerXchangeApi.getBrewery(breweryUntappdId);
      returnPromise
        .then((response) => {
          updateKnownBreweries(response);
        })
        .catch((error: any) => {
          console.warn('http error:', error, error.stack);
          setState((state) => ({ ...state, isRefreshing: false }));
          throw error;
        });
      return returnPromise;
    } catch (error) {
      console.warn('caught error:', error, error.stack);
      setState((state) => ({ ...state, isRefreshing: false }));
      throw error;
    }
  };

  const getBreweryDetails = (breweryUntappdId: string): Promise<Brewery> => {
    try {
      const returnPromise = beerXchangeApi.getBreweryDetails(breweryUntappdId);
      return returnPromise;
    } catch (error) {
      console.warn('caught error:', error, error.stack);
      throw error;
    }
  };

  const getBreweryBeers = (breweryUntappdId: string): Promise<Beer[]> => {
    try {
      setState((state) => ({ ...state, isRefreshing: true }));
      const returnPromise = beerXchangeApi.getBreweryBeers(breweryUntappdId);
      returnPromise
        .then((response) => {
          updateKnownBeers(response);
        })
        .catch((error: any) => {
          console.warn('http error:', error, error.stack);
          setState((state) => ({ ...state, isRefreshing: false }));
          throw error;
        });
      return returnPromise;
    } catch (error) {
      console.warn('caught error:', error, error.stack);
      setState((state) => ({ ...state, isRefreshing: false }));
      throw error;
    }
  };

  const claimBeer = (beerUntappdId: string): void => {
    setState((state) => ({ ...state, isRefreshing: true }));
    const returnPromise = beerXchangeApi.claimBeer(beerUntappdId);
    returnPromise
      .then(() => {
        refreshLoggedInUserClaims();
      })
      .catch((error: any) => {
        console.warn('http error:', error, error.stack);
        setState((state) => ({ ...state, isRefreshing: false }));
        throw error;
      });
  };

  const unclaimBeer = (beerId: string): void => {
    setState((state) => ({ ...state, isRefreshing: true }));
    const returnPromise = beerXchangeApi.removeBeer(beerId);
    returnPromise
      .then(() => {
        refreshLoggedInUserClaims();
      })
      .catch((error: any) => {
        console.warn('http error:', error, error.stack);
        throw error;
      });
  };

  const getUsers = (): Promise<BeerXchangeUser[]> => {
    try {
      setState((state) => ({ ...state, isRefreshing: true }));
      const returnPromise = beerXchangeApi.listUsers();
      returnPromise.then((response) => {
        updateKnownUsers(response);
      });
      return returnPromise;
    } catch (error) {
      console.warn('caught error:', error, error.stack);
      throw error;
    }
  };

  const getUser = (username: string): Promise<BeerXchangeUser> => {
    try {
      setState((state) => ({ ...state, isRefreshing: true }));
      const returnPromise = beerXchangeApi.getUserInfo(username);
      return returnPromise;
    } catch (error) {
      console.warn('caught error:', error, error.stack);
      throw error;
    }
  };

  const getTrade = (tradeId: string): Promise<Trade> => {
    try {
      setState((state) => ({ ...state, isRefreshing: true }));
      const returnPromise = beerXchangeApi.getTrade(tradeId);
      returnPromise.then((response) => {
        updateKnownCurrentTrades(response);
      });
      return returnPromise;
    } catch (error) {
      console.warn('caught error:', error, error.stack);
    }
  };

  const getTradeList = (userId: string): Promise<Trade[]> => {
    try {
      setState((state) => ({ ...state, isRefreshing: true }));
      const returnPromise = beerXchangeApi.listTrades(userId);
      returnPromise.then((response) => {
        updateKnownCurrentTrades(response);
      });
      return returnPromise;
    } catch (error) {
      console.warn('caught error:', error, error.stack);
    }
  };

  const getTradeHistory = (userId: string): Promise<Trade[]> => {
    try {
      setState((state) => ({ ...state, isRefreshing: true }));
      const returnPromise = beerXchangeApi.listTradeHistory(userId);
      returnPromise.then((response) => {
        updateKnownHistoryTrades(response);
      });
      return returnPromise;
    } catch (error) {
      console.warn('caught error:', error, error.stack);
      throw error;
    }
  };

  const updateUserSeeking = async (seeking: Seeking) => {
    setState((state) => ({ ...state, userSeekingBeers: seeking.beers }));
    setState((state) => ({ ...state, userSeekingBreweries: seeking.breweries }));
  };

  const getSeeking = (userId: string): Promise<Seeking> => {
    try {
      setState((state) => ({ ...state, isRefreshing: true }));
      const returnPromise = beerXchangeApi.getUserSeeking(userId);
      returnPromise.then((response) => {
        updateKnownBeers(response.beers);
        if (loggedInUser && loggedInUser.id === userId) {
          updateUserSeeking(response);
        }
      });
      return returnPromise;
    } catch (error) {
      console.warn('caught error:', error, error.stack);
      throw error;
    }
  };

  const updateUserOffering = async (offering: Offering) => {
    setState((state) => ({ ...state, userOfferingBeers: offering.offeredBeers }));
    setState((state) => ({ ...state, userOfferingBreweries: offering.offeredBreweries }));
  };

  const getOffering = (userId: string): Promise<Offer> => {
    try {
      setState((state) => ({ ...state, isRefreshing: true }));
      const returnPromise = beerXchangeApi.getUserOffering(userId);
      returnPromise.then((response) => {
        updateKnownBeers(response.offeredBeers);
        if (loggedInUser && loggedInUser.id === userId) {
          updateUserOffering(response);
        }
      });
      return returnPromise;
    } catch (error) {
      console.warn('caught error:', error, error.stack);
      throw error;
    }
  };

  const getClaimsForUser = (username: string): Promise<BeerWithClaims[]> => {
    try {
      setState((state) => ({ ...state, isRefreshing: true }));
      const returnPromise = beerXchangeApi.getClaimsForUser(username);
      returnPromise.then((response) => {
        updateKnownBeers(response);
        if (loggedInUser && username === loggedInUser.name) {
          setState((state) => ({ ...state, loggedInUserClaims: response }));
          updateUserLists(response);
        }
      });
      return returnPromise;
    } catch (error) {
      console.warn('caught error:', error, error.stack);
      throw error;
    }
  };

  const getConsumedClaimsForUser = (username: string): Promise<BeerWithClaims[]> => {
    try {
      setState((state) => ({ ...state, isRefreshing: true }));
      const returnPromise = beerXchangeApi.getConsumedClaimsForUser(username);
      returnPromise.then((response) => {
        updateKnownBeers(response);
      });
      return returnPromise;
    } catch (error) {
      console.warn('caught error:', error, error.stack);
      throw error;
    }
  };

  const getCollectionByID = (id: string): Promise<BeerXchangeCollection> => {
    try {
      setState((state) => ({ ...state, isRefreshing: true }));
      const returnPromise = beerXchangeApi.getCollectionByID(id);
      return returnPromise;
    } catch (error) {
      console.warn('caught error:', error, error.stack);
      throw error;
    }
  };

  const getCollectionForUser = (username: string): Promise<BeerXchangeCollection> => {
    try {
      setState((state) => ({ ...state, isRefreshing: true }));
      const returnPromise = beerXchangeApi.getCollectionForUser(username);
      return returnPromise;
    } catch (error) {
      console.warn('caught error:', error, error.stack);
      throw error;
    }
  };

  const getUserEvents = (userId: string): Promise<UserEvents> => {
    try {
      setState((state) => ({ ...state, isRefreshing: true }));
      const returnPromise = beerXchangeApi.getUserEvents(userId);
      returnPromise.then((response) => {
        refreshLoggedInUserEvents(response);
      });
      return returnPromise;
    } catch (error) {
      console.warn('caught error:', error, error.stack);
      throw error;
    }
  };

  const dismissEvent = (eventId: string): Promise<UserEvents> => {
    try {
      const returnPromise = beerXchangeApi.dismissEvent(eventId);
      returnPromise.then((response) => {
        refreshLoggedInUserEvents(response);
      });
      return returnPromise;
    } catch (error) {
      console.warn('caught error:', error, error.stack);
      throw error;
    }
  };

  const undoDismissEvent = (eventId: string): Promise<UserEvents> => {
    try {
      const returnPromise = beerXchangeApi.undoDismissEvent(eventId);
      returnPromise.then((response) => {
        refreshLoggedInUserEvents(response);
      });
      return returnPromise;
    } catch (error) {
      console.warn('caught error:', error, error.stack);
      throw error;
    }
  };

  const seekBeer = (beerUntappdId: string): Promise<Beer> => {
    try {
      setState((state) => ({ ...state, isRefreshing: true }));
      const returnPromise = beerXchangeApi.seekBeer(beerUntappdId);
      return returnPromise;
    } catch (error) {
      console.warn('caught error:', error, error.stack);
      throw error;
    }
  };

  const offerBeer = (beerUntappdId: string): Promise<Beer> => {
    try {
      setState((state) => ({ ...state, isRefreshing: true }));
      const returnPromise = beerXchangeApi.offerBeer(beerUntappdId);
      return returnPromise;
    } catch (error) {
      console.warn('caught error:', error, error.stack);
      throw error;
    }
  };

  const acceptOffer = (offerId: string, notes: string): Promise<Offer> => {
    try {
      setState((state) => ({ ...state, isRefreshing: true }));
      const returnPromise = beerXchangeApi.acceptOffer(offerId, notes);
      return returnPromise;
    } catch (error) {
      console.warn('caught error:', error, error.stack);
      throw error;
    }
  };

  const cancelOffer = (offerId: string, notes: string): Promise<Offer> => {
    try {
      setState((state) => ({ ...state, isRefreshing: true }));
      const returnPromise = beerXchangeApi.cancelOffer(offerId, notes);
      return returnPromise;
    } catch (error) {
      console.warn('caught error:', error, error.stack);
      throw error;
    }
  };

  const rejectOffer = (offerId: string, notes: string): Promise<Offer> => {
    try {
      setState((state) => ({ ...state, isRefreshing: true }));
      const returnPromise = beerXchangeApi.rejectOffer(offerId, notes);
      return returnPromise;
    } catch (error) {
      console.warn('caught error:', error, error.stack);
      throw error;
    }
  };

  const makeOffer = (
    userName: string,
    offeredBeers: string[],
    requestedBeers: string[],
    notes: string,
    ttl: string
  ): Promise<Offer> => {
    try {
      setState((state) => ({ ...state, isRefreshing: true }));
      const returnPromise = beerXchangeApi.makeOffer(
        userName,
        offeredBeers,
        requestedBeers,
        notes,
        ttl
      );
      return returnPromise;
    } catch (error) {
      console.warn('caught error:', error, error.stack);
      throw error;
    }
  };

  const updateOffer = (
    offerId: string,
    tradeId: string,
    offeredBeers: string[],
    requestedBeers: string[],
    notes: string,
    ttl: string
  ): Promise<Offer> => {
    try {
      setState((state) => ({ ...state, isRefreshing: true }));
      const returnPromise = beerXchangeApi.updateOffer(
        offerId,
        tradeId,
        offeredBeers,
        requestedBeers,
        notes,
        ttl
      );
      return returnPromise;
    } catch (error) {
      console.warn('caught error:', error, error.stack);
      throw error;
    }
  };

  const beginShipping = (
    offerId: string,
    shippingType: string,
    carrier: string,
    trackingNumber: string,
    shippingCost: string,
    notes: string
  ): Promise<Shipping> => {
    try {
      setState((state) => ({ ...state, isRefreshing: true }));
      const returnPromise = beerXchangeApi.beginShipping(
        offerId,
        shippingType,
        carrier,
        trackingNumber,
        shippingCost,
        notes
      );
      return returnPromise;
    } catch (error) {
      console.warn('caught error:', error, error.stack);
      throw error;
    }
  };

  const getShippingForTrade = (tradeId: string): Promise<Shipping[]> => {
    try {
      const returnPromise = beerXchangeApi.getShippingForTrade(tradeId);
      return returnPromise;
    } catch (error) {
      console.warn('caught error:', error, error.stack);
      throw error;
    }
  };

  const markComplete = (
    tradeId: string,
    ratingValue: string,
    timelyShipping: string,
    communicatesWell: string,
    helpful: string,
    wouldTradeAgain: string,
    notes: string
  ): Promise<Trade> => {
    try {
      setState((state) => ({ ...state, isRefreshing: true }));
      const returnPromise = beerXchangeApi.markComplete(
        tradeId,
        ratingValue,
        timelyShipping,
        communicatesWell,
        helpful,
        wouldTradeAgain,
        notes
      );
      return returnPromise;
    } catch (error) {
      console.warn('caught error:', error, error.stack);
      throw error;
    }
  };

  const markException = (
    tradeId: string,
    ratingValue: string,
    timelyShipping: string,
    communicatesWell: string,
    helpful: string,
    wouldTradeAgain: string,
    notes: string
  ): Promise<Trade> => {
    try {
      setState((state) => ({ ...state, isRefreshing: true }));
      const returnPromise = beerXchangeApi.markException(
        tradeId,
        ratingValue,
        timelyShipping,
        communicatesWell,
        helpful,
        wouldTradeAgain,
        notes
      );
      return returnPromise;
    } catch (error) {
      console.warn('caught error:', error, error.stack);
      throw error;
    }
  };

  const getUserRatings = (userId: string): Promise<Rating[]> => {
    try {
      const returnPromise = beerXchangeApi.getUserRatings(userId);
      return returnPromise;
    } catch (error) {
      console.warn('caught error:', error, error.stack);
      throw error;
    }
  };

  const getTradeRatings = (tradeId: string): Promise<Rating[]> => {
    try {
      const returnPromise = beerXchangeApi.getTradeRatings(tradeId);
      return returnPromise;
    } catch (error) {
      console.warn('caught error:', error, error.stack);
      throw error;
    }
  };

  const exportCollection = (collectionId: string): Promise<string> => {
    try {
      const returnPromise = beerXchangeApi.exportCollection(collectionId);
      return returnPromise;
    } catch (error) {
      console.warn('caught error:', error, error.stack);
      throw error;
    }
  };

  const importCollection = (collectionId: string, file: File): Promise<any[]> => {
    try {
      const returnPromise = beerXchangeApi.importCollection(collectionId, file);
      return returnPromise;
    } catch (error) {
      console.warn('caught error:', error, error.stack);
      throw error;
    }
  };

  const refreshCollection = (collectionId: string): Promise<string> => {
    try {
      const returnPromise = beerXchangeApi.refreshCollection(collectionId);
      return returnPromise;
    } catch (error) {
      console.warn('caught error:', error, error.stack);
      throw error;
    }
  };

  const updateCollection = (
    collectionId: string,
    name: string,
    location: string,
    isPublic: boolean,
    showValue: boolean,
    defaultCurrency: string,
    showConsumed: boolean
  ): Promise<BeerXchangeCollection> => {
    try {
      const returnPromise = beerXchangeApi.updateCollection(
        collectionId,
        name,
        location,
        isPublic,
        showValue,
        defaultCurrency,
        showConsumed
      );
      returnPromise.then((response) => {
        setState((state) => ({ ...state, loggedInUserCollection: response }));
      });
      return returnPromise;
    } catch (error) {
      console.warn('caught error:', error, error.stack);
      throw error;
    }
  };

  const updateUserInfo = (
    userName: string,
    firstName: string,
    lastName: string,
    address: string,
    city: string,
    state: string,
    country: string,
    zipcode: string,
    defaultView: string
  ): Promise<BeerXchangeUser> => {
    try {
      const returnPromise = beerXchangeApi.updateUserInfo(
        userName,
        firstName,
        lastName,
        address,
        city,
        state,
        country,
        zipcode,
        defaultView
      );
      return returnPromise;
    } catch (error) {
      console.warn('caught error:', error, error.stack);
      throw error;
    }
  };

  const deleteUser = (userId: string): void => {
    try {
      const returnPromise = beerXchangeApi.deleteUser(userId);
      return returnPromise;
    } catch (error) {
      console.warn('caught error:', error, error.stack);
      throw error;
    }
  };

  const drinkBeer = (claimedBeerId: string): void => {
    try {
      const returnPromise = beerXchangeApi.drinkBeer(claimedBeerId);
      returnPromise.then((response) => {
        refreshLoggedInUserClaims();
      });
      return returnPromise;
    } catch (error) {
      console.warn('caught error:', error, error.stack);
      throw error;
    }
  };

  const undrinkBeer = (claimedBeerId: string): void => {
    try {
      const returnPromise = beerXchangeApi.undrinkBeer(claimedBeerId);
      return returnPromise;
    } catch (error) {
      console.warn('caught error:', error, error.stack);
      throw error;
    }
  };

  const editClaimedBeer = (
    claimedBeerId: string,
    count: string,
    denomination: string,
    price: string,
    size: string,
    bottled: string,
    added: string,
    drink_by: string,
    notes: string,
    location: string,
    purchased: string
  ): Promise<ClaimedBeer> => {
    try {
      const returnPromise = beerXchangeApi.editClaimedBeer(
        claimedBeerId,
        count,
        denomination,
        price,
        size,
        bottled,
        added,
        drink_by,
        notes,
        location,
        purchased
      );
      returnPromise.then((response) => {
        refreshLoggedInUserClaims();
      });
      return returnPromise;
    } catch (error) {
      console.warn('caught error:', error, error.stack);
      throw error;
    }
  };

  const unseekBeer = (beerUntappdId: string): void => {
    try {
      const returnPromise = beerXchangeApi.unseekBeer(beerUntappdId);
      return returnPromise;
    } catch (error) {
      console.warn('caught error:', error, error.stack);
      throw error;
    }
  };

  const unofferBeer = (beerUntappdId: string): void => {
    try {
      const returnPromise = beerXchangeApi.unofferBeer(beerUntappdId);
      return returnPromise;
    } catch (error) {
      console.warn('caught error:', error, error.stack);
      throw error;
    }
  };

  const matchingBeers = (beerUntappdId: string): Promise<Seeking[]> => {
    try {
      const returnPromise = beerXchangeApi.matchingBeers(beerUntappdId);
      returnPromise.then((response) => {
        response.forEach((match) => {
          updateKnownUsers([match.user]);
        });
      });
      return returnPromise;
    } catch (error) {
      console.warn('caught error:', error, error.stack);
      throw error;
    }
  };

  const matchingSeekingBeers = (beerUntappdId: string): Promise<Offering[]> => {
    try {
      const returnPromise = beerXchangeApi.matchingSeekingBeers(beerUntappdId);
      returnPromise.then((response) => {
        response.forEach((match) => {
          updateKnownUsers([match.user]);
        });
      });
      return returnPromise;
    } catch (error) {
      console.warn('caught error:', error, error.stack);
      throw error;
    }
  };

  const getBeer = (beerUntappdId: string): Promise<Beer> => {
    try {
      const returnPromise = beerXchangeApi.getBeer(beerUntappdId);
      returnPromise.then((response) => {
        if (response !== undefined) {
          updateKnownBeers([response]);
        }
      });
      return returnPromise;
    } catch (error) {
      console.warn('caught error:', error, error.stack);
      throw error;
    }
  };

  const getBeerDetails = (beerUntappdId: string): Promise<Beer> => {
    try {
      const returnPromise = beerXchangeApi.getBeerDetails(beerUntappdId);
      return returnPromise;
    } catch (error) {
      console.warn('caught error:', error, error.stack);
      throw error;
    }
  };

  const getMostWanted = async (): Promise<Beer[]> => {
    try {
      const returnPromise = beerXchangeApi.getMostWanted();
      returnPromise.then((response) => {
        updateKnownBeers(response);
      });
      return returnPromise;
    } catch (error) {
      console.warn('caught error:', error, error.stack);
      throw error;
    }
  };

  const getMostOffered = async (): Promise<Beer[]> => {
    try {
      const returnPromise = beerXchangeApi.getMostOffered();
      returnPromise.then((response) => {
        updateKnownBeers(response);
      });
      return returnPromise;
    } catch (error) {
      console.warn('caught error:', error, error.stack);
      throw error;
    }
  };

  const getRecentOffers = (): Promise<Beer[]> => {
    try {
      const returnPromise = beerXchangeApi.getRecentOffers();
      returnPromise.then((response) => {
        updateKnownBeers(response);
      });
      return returnPromise;
    } catch (error) {
      console.warn('caught error:', error, error.stack);
      throw error;
    }
  };

  const getDirectoryIndex = (): Promise<DirectoryIndex> => {
    try {
      return beerXchangeApi.getDirectoryIndex();
    } catch (error) {
      console.warn('caught error:', error, error.stack);
      throw error;
    }
  };

  const getDirectoryPage = (letter: string, page: number): Promise<DirectoryPage> => {
    try {
      return beerXchangeApi.getDirectoryPage(letter, page);
    } catch (error) {
      console.warn('caught error:', error, error.stack);
      throw error;
    }
  };

  const seekBrewery = (untappdId: string): Promise<Brewery> => {
    try {
      setState((state) => ({ ...state, isRefreshing: true }));
      const returnPromise = beerXchangeApi.seekBrewery(untappdId);
      return returnPromise;
    } catch (error) {
      console.warn('caught error:', error, error.stack);
      throw error;
    }
  };

  const unseekBrewery = (untappdId: string): Promise<Brewery> => {
    try {
      setState((state) => ({ ...state, isRefreshing: true }));
      const returnPromise = beerXchangeApi.unseekBrewery(untappdId);
      return returnPromise;
    } catch (error) {
      console.warn('caught error:', error, error.stack);
      throw error;
    }
  };

  const offerBrewery = (untappdId: string): Promise<Brewery> => {
    try {
      setState((state) => ({ ...state, isRefreshing: true }));
      const returnPromise = beerXchangeApi.offerBrewery(untappdId);
      return returnPromise;
    } catch (error) {
      console.warn('caught error:', error, error.stack);
      throw error;
    }
  };

  const unofferBrewery = (untappdId: string): Promise<Brewery> => {
    try {
      setState((state) => ({ ...state, isRefreshing: true }));
      const returnPromise = beerXchangeApi.unofferBrewery(untappdId);
      return returnPromise;
    } catch (error) {
      console.warn('caught error:', error, error.stack);
      throw error;
    }
  };

  const isUserSeekingBrewery = (brewery: Brewery) => {
    var result = false;
    if (brewery && state.userSeekingBreweries && state.userSeekingBreweries.length > 0) {
      result = state.userSeekingBreweries.some(
        (seeking) => seeking.untappdId === brewery.untappdId
      );
    }
    return result;
  };

  const isUserOfferingBrewery = (brewery: Brewery) => {
    var result = false;
    if (brewery && state.userOfferingBreweries && state.userOfferingBreweries.length > 0) {
      result = state.userOfferingBreweries.some(
        (offering) => offering.untappdId === brewery.untappdId
      );
    }
    return result;
  };

  const matchingBreweries = (untappdId: string): Promise<Seeking[]> => {
    try {
      setState((state) => ({ ...state, isRefreshing: true }));
      const returnPromise = beerXchangeApi.matchingBreweries(untappdId);
      return returnPromise;
    } catch (error) {
      console.warn('caught error:', error, error.stack);
      throw error;
    }
  };

  const matchingSeekingBreweries = (untappdId: string): Promise<Offering[]> => {
    try {
      setState((state) => ({ ...state, isRefreshing: true }));
      const returnPromise = beerXchangeApi.matchingSeekingBreweries(untappdId);
      return returnPromise;
    } catch (error) {
      console.warn('caught error:', error, error.stack);
      throw error;
    }
  };

  const getPrices = (beerUntappdId: string): void => {
    try {
      const returnPromise = beerXchangeApi.getPrices(beerUntappdId);
      return returnPromise;
    } catch (error) {
      console.warn('caught error:', error, error.stack);
      throw error;
    }
  };

  const getUntappdUserInfo = (username: string): Promise<UntappdUser> => {
    try {
      setState((state) => ({ ...state, isRefreshing: true }));
      const returnPromise = beerXchangeApi.getUntappdUserInfo(username);
      return returnPromise;
    } catch (error) {
      console.warn('caught error:', error, error.stack);
      throw error;
    }
  };

  return (
    <BeerXchangeContext.Provider
      value={{
        ...state,
        refreshLoggedInUser,
        refreshLoggedInUserCollection,
        refreshLoggedInUserClaims,
        refreshLoggedInUserEvents,
        searchBeers,
        searchBreweries,
        getBreweryBeers,
        claimBeer,
        unclaimBeer,
        getUser,
        getUsers,
        getTrade,
        getTradeList,
        getTradeHistory,
        getSeeking,
        getOffering,
        getClaimsForUser,
        getConsumedClaimsForUser,
        getCollectionForUser,
        getCollectionByID,
        getUserEvents,
        dismissEvent,
        undoDismissEvent,
        seekBeer,
        offerBeer,
        acceptOffer,
        cancelOffer,
        rejectOffer,
        makeOffer,
        updateOffer,
        beginShipping,
        markComplete,
        markException,
        getShippingForTrade,
        getUserRatings,
        getTradeRatings,
        refreshCollection,
        exportCollection,
        importCollection,
        updateCollection,
        updateUserInfo,
        deleteUser,
        drinkBeer,
        undrinkBeer,
        editClaimedBeer,
        unseekBeer,
        unofferBeer,
        isUserSeekingBeer,
        isUserOfferingBeer,
        matchingBeers,
        matchingSeekingBeers,
        getBeer,
        removePreviousSearch,
        getBrewery,
        getMostWanted,
        getMostOffered,
        getRecentOffers,
        getDirectoryIndex,
        getDirectoryPage,
        seekBrewery,
        unseekBrewery,
        offerBrewery,
        unofferBrewery,
        clearPreviousSearches,
        isUserSeekingBrewery,
        isUserOfferingBrewery,
        matchingBreweries,
        matchingSeekingBreweries,
        getBeerDetails,
        getBreweryDetails,
        getPrices,
        setCollectionFilter,
        setCollectionPredicate,
        setCollectionReverse,
        getUntappdUserInfo,
      }}
    >
      {children}
    </BeerXchangeContext.Provider>
  );
};
